import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { SectionLabel, SectionHeading } from './ui/UI';
const faqs = [{
  q: 'What is Periscan?',
  a: 'Periscan is a continuous security validation platform being reborn in 2026. It helps modern companies identify realistic attack paths, validate AI application risk, prioritize remediation, verify fixes, and generate reusable security proof.'
}, {
  q: 'How does Periscan work?',
  a: 'Periscan connects to the systems that define your attack surface — cloud, identity, SaaS, code repositories, domains, internal environments, and AI application endpoints. It builds an AI-driven evidence model of your environment, identifies realistic paths an attacker could use, validates which risks are real or reachable, and produces a plain-English report with evidence, remediation steps, and verification guidance.'
}, {
  q: 'Is Periscan a vulnerability scanner?',
  a: 'No. Periscan uses security signals, but the product is not a raw vulnerability scanner. The goal is to show realistic paths to compromise, explain what matters, recommend fixes, and verify whether the risk is gone.'
}, {
  q: 'Is Periscan a pentest?',
  a: 'Periscan is not a traditional consulting pentest. It is being built as self-serve continuous validation software that can generate recurring attack-path reports, AI app validation reports, remediation guidance, and verification evidence.'
}, {
  q: 'Is Periscan a BAS or AEV platform?',
  a: 'Periscan includes concepts from breach and attack simulation, adversarial exposure validation, attack-path analysis, and continuous exposure management. But Periscan is packaged around a simpler outcome: find what can compromise you, fix what matters, prove it is gone.'
}, {
  q: 'What is an attack-path report?',
  a: 'An attack-path report shows the realistic paths that could lead to compromise. Instead of listing hundreds or thousands of raw findings, it explains the few paths that matter most, the evidence behind them, the business impact, what to fix, and how to verify closure.'
}, {
  q: 'What is AI security validation?',
  a: 'AI security validation tests AI applications, RAG systems, copilots, agents, and LLM workflows for risks such as prompt injection, sensitive data leakage, unsafe tool access, agent over-permissioning, system prompt exposure, and guardrail drift.'
}, {
  q: 'Does Periscan replace existing tools?',
  a: 'Not initially. Periscan is designed to sit above existing security signals and turn them into validated paths, remediation guidance, and proof.'
}, {
  q: 'Is Periscan safe to run?',
  a: 'Yes. Periscan emphasizes scoped testing, read-only checks where possible, customer-controlled permissions, no destructive actions, no real data exfiltration, audit logs, approval gates, and clear test boundaries.'
}, {
  q: 'Will Periscan be self-serve?',
  a: 'Yes. Periscan is being designed as a self-serve product with SaaS-style pricing and fast time to first report. Some advanced validation workflows may require guided onboarding during early access.'
}, {
  q: 'Is Periscan only for small companies?',
  a: 'No. Periscan is designed for modern teams that want continuous validation without enterprise complexity. That can include startups, SaaS, AI companies, mid-market organizations, MSSPs, vCISOs, Fortune 10,000 business units, subsidiaries, and enterprise AI app teams.'
}, {
  q: 'When will Periscan be available?',
  a: 'Periscan is preparing for early access in 2026. The first release will focus on attack-path reports, AI app validation, fix verification, and evidence-ready reporting.'
}, {
  q: 'How much will Periscan cost?',
  a: 'Periscan is designed around transparent SaaS-style pricing. Preview pricing starts with a Snapshot report and expands into Team, Business, Scale, MSSP, and Enterprise plans. Final pricing may evolve during early access.'
}];
export function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-358">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-359">
        <SectionLabel number="23" label="FAQ" className="mb-8" data-id="element-360" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-361">
          <div className="lg:col-span-4" data-id="element-362">
            <SectionHeading size="lg" data-id="element-363">Common questions.</SectionHeading>
            <p className="mt-6 text-base text-white/60 leading-relaxed" data-id="element-364">
              Everything you need to know about Periscan, early access, and what
              to expect during the 2026 release.
            </p>
          </div>

          <div className="lg:col-span-8" data-id="element-365">
            <div className="border-t border-white/5" data-id="element-366">
              {faqs.map((faq, i) => {
              const isOpen = open === i;
              return <div key={i} className="border-b border-white/5" data-id="element-367">
                    <button onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-6 py-6 text-left group" data-id="element-368">
                      <span className="text-lg md:text-xl font-medium text-white pr-4 group-hover:text-[#1a8fd4] transition-colors" data-id="element-369">
                        {faq.q}
                      </span>
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-all ${isOpen ? 'bg-[#1a8fd4] border-[#1a8fd4] rotate-45' : 'border-white/10 group-hover:border-white/30'}`} data-id="element-370">
                        <Plus className={`w-4 h-4 transition-colors ${isOpen ? 'text-white' : 'text-white/60'}`} data-id="element-371" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-372">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} className="overflow-hidden" data-id="element-373">
                          <p className="pb-6 pr-12 text-base text-white/60 leading-relaxed" data-id="element-374">
                            {faq.a}
                          </p>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}